import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { signupUser } from '../../redux/actions/userActions';
import { isProfane, cleanProfanity } from '../../util/filterProfanity';
import chesssLogo from '../../images/chessslogo.png';
import mansalva from '../../images/Mansalva/Mansalva-Regular.ttf';
import axios from 'axios';
// Rate limiting
import { checkRateLimit } from '../../util/rateLimiter';
import { getRateLimit, buildGlobalRateKey } from '../../util/rateLimitConfig';

const SignupMobile = ({ signupUser, UI }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    handle: '',
    agreedToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [characters, setCharacters] = useState(0);
  const [rateLimitError, setRateLimitError] = useState('');
  const history = useHistory();
  let usernameTimeout = null;

  // Update errors from Redux
  useEffect(() => {
    if (UI.errors) {
      setErrors(UI.errors);
    }
  }, [UI.errors]);

  // Check username availability with debounce
  const checkUsernameAvailability = (username) => {
    if (usernameTimeout) {
      clearTimeout(usernameTimeout);
    }

    if (!username || username.length < 3) return;

    setCheckingUsername(true);

    usernameTimeout = setTimeout(() => {
      axios.get(`/user/${username.toLowerCase()}/check`)
        .then(res => {
          if (res.data && res.data.exists !== undefined) {
            const exists = res.data.exists;
            if (exists) {
              setErrors(prev => ({
                ...prev,
                handle: 'This username is already taken'
              }));
            } else {
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.handle;
                return newErrors;
              });
            }
          }
          setCheckingUsername(false);
        })
        .catch(err => {
          console.error('Error checking username:', err);
          setCheckingUsername(false);
        });
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Handle username field specially
    if (name === 'handle') {
      if (value.length > 15) return;
      
      // First apply profanity filtering, then alphanumeric-only filter
      const profanityFiltered = cleanProfanity(value);
      const alphanumericOnly = profanityFiltered.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: alphanumericOnly }));
      setCharacters(alphanumericOnly.length);
      
      if (alphanumericOnly.length > 2) {
        checkUsernameAvailability(alphanumericOnly);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Client-side validation
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords must match';
    }

    if (!formData.handle) {
      newErrors.handle = 'Username is required';
    } else if (formData.handle.length > 15) {
      newErrors.handle = 'Username cannot exceed 15 characters';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.handle)) {
      newErrors.handle = 'Username can only contain letters and numbers';
    } else if (isProfane(formData.handle)) {
      newErrors.handle = 'Inappropriate language not allowed in usernames';
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the Terms and Conditions';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Rate limiting check for signup attempts
    const config = getRateLimit('SIGNUP_ATTEMPT', 'free');
    const rateKey = buildGlobalRateKey('anonymous', 'signup_attempt');
    
    const isLimited = checkRateLimit({
      key: rateKey,
      limit: config.limit,
      windowMs: config.windowMs
    });

    if (isLimited) {
      setRateLimitError('Too many signup attempts. Please wait then try again.');
      setErrors({});
      return;
    }

    // Clear any previous rate limit errors
    setRateLimitError('');

    // Submit signup
    const newUserData = {
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      handle: formData.handle,
      recaptchaToken: 'dummy-token'
    };

    signupUser(newUserData, history);
  };

  const isFormValid = 
    formData.email.trim() !== '' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) &&
    formData.password.trim() !== '' &&
    formData.confirmPassword.trim() !== '' &&
    formData.password === formData.confirmPassword &&
    formData.handle.trim().length >= 3 &&
    !checkingUsername &&
    formData.agreedToTerms &&
    Object.keys(errors).length === 0;

  const isDisabled = UI.loading || !isFormValid;

  return (
    <div style={{
      width: '100vw',
      height: '100dvh', // Full viewport height
      backgroundColor: '#000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center', // Center content vertically
      padding: '20px',
      paddingTop: 'calc(env(safe-area-inset-top) + 20px)', // iPhone notch safe area + padding
      paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)', // iPhone gesture area + padding
      boxSizing: 'border-box',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <style>
        {`
          @font-face {
            font-family: 'Mansalva';
            src: url(${mansalva}) format('truetype');
            font-weight: normal;
            font-style: normal;
          }
        `}
      </style>
      {/* Logo and Title */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        flexShrink: 0
      }}>
        <img
          src={chesssLogo}
          alt="Chess Social"
          style={{
            width: '80px',
            height: '80px',
            marginBottom: '15px'
          }}
        />
        <h3 style={{
          fontFamily: 'Mansalva, cursive',
          color: 'gold',
          fontSize: '28px',
          fontWeight: 'normal',
          margin: 0,
          letterSpacing: '1px'
        }}>
          Create a Free Account
        </h3>
      </div>

      {/* Signup Form */}
      <form onSubmit={handleSubmit} style={{
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        flexShrink: 0
      }}>
        {/* Rate limit error message */}
        {rateLimitError && (
          <div style={{
            color: 'white',
            backgroundColor: '#ff6b6b',
            padding: '12px 16px',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {rateLimitError}
          </div>
        )}

        {/* Email Field */}
        <div>
          <label style={{
            color: '#ccc',
            fontSize: '14px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Email Address
          </label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: '#ffffff',
              border: errors.email ? '2px solid #ff0050' : '2px solid #ccc',
              borderRadius: '8px',
              color: '#000000',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {errors.email && (
            <div style={{
              color: '#ff0050',
              fontSize: '14px',
              marginTop: '5px'
            }}>
              {errors.email}
            </div>
          )}
        </div>

        {/* Username Field */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              color: '#ccc',
              fontSize: '14px'
            }}>
              Username
            </label>
            {checkingUsername ? (
              <div style={{ color: '#3066be', fontSize: '12px' }}>
                Checking...
              </div>
            ) : errors.handle ? (
              <div style={{ color: '#ff0050', fontSize: '12px' }}>
                {errors.handle}
              </div>
            ) : formData.handle && formData.handle.length > 2 ? (
              <div style={{ color: '#4caf50', fontSize: '12px' }}>
                Available
              </div>
            ) : formData.handle ? (
              <div style={{ color: '#888', fontSize: '12px' }}>
                3+ chars needed
              </div>
            ) : null}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              name="handle"
              type="text"
              value={formData.handle}
              onChange={handleChange}
              placeholder="Choose a username"
              maxLength={15}
              style={{
                width: '100%',
                padding: '12px 16px',
                paddingRight: '50px',
                backgroundColor: '#ffffff',
                border: errors.handle ? '2px solid #ff0050' : '2px solid #ccc',
                borderRadius: '8px',
                color: '#000000',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: characters >= 13 ? '#ff9900' : '#888'
            }}>
              {15 - characters}
            </div>
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label style={{
            color: '#ccc',
            fontSize: '14px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Password
          </label>
          <input
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a secure password"
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: '#ffffff',
              border: errors.password ? '2px solid #ff0050' : '2px solid #ccc',
              borderRadius: '8px',
              color: '#000000',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {errors.password && (
            <div style={{
              color: '#ff0050',
              fontSize: '14px',
              marginTop: '5px'
            }}>
              {errors.password}
            </div>
          )}
        </div>

        {/* Confirm Password Field */}
        <div>
          <label style={{
            color: '#ccc',
            fontSize: '14px',
            marginBottom: '8px',
            display: 'block'
          }}>
            Confirm Password
          </label>
          <input
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: '#ffffff',
              border: errors.confirmPassword ? '2px solid #ff0050' : '2px solid #ccc',
              borderRadius: '8px',
              color: '#000000',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {errors.confirmPassword && (
            <div style={{
              color: '#ff0050',
              fontSize: '14px',
              marginTop: '5px'
            }}>
              {errors.confirmPassword}
            </div>
          )}
        </div>

        {/* Terms Agreement */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginTop: '10px'
        }}>
          <input
            name="agreedToTerms"
            type="checkbox"
            checked={formData.agreedToTerms}
            onChange={handleChange}
            style={{
              width: '18px',
              height: '18px',
              marginTop: '2px'
            }}
          />
          <label style={{
            color: '#ccc',
            fontSize: '14px',
            lineHeight: '1.4',
            flex: 1
          }}>
            I agree to the{' '}
            <Link to="/mobile/terms" style={{ color: 'gold', textDecoration: 'underline' }}>
              Terms
            </Link>
            {' '}and{' '}
            <Link to="/mobile/privacy" style={{ color: 'gold', textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
          </label>
        </div>
        {errors.agreedToTerms && (
          <div style={{
            color: '#ff0050',
            fontSize: '14px',
            marginTop: '-10px'
          }}>
            {errors.agreedToTerms}
          </div>
        )}

        {/* General Error */}
        {errors.general && (
          <div style={{
            color: '#ff0050',
            fontSize: '14px',
            textAlign: 'center',
            backgroundColor: 'rgba(255, 0, 80, 0.1)',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 0, 80, 0.3)'
          }}>
            {errors.general}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isDisabled}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: isDisabled ? '#666' : '#ff0050',
            background: isDisabled ? '#666' : 'linear-gradient(135deg, #ff0050, #ff4081)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: isDisabled ? 'default' : 'pointer',
            opacity: isDisabled ? 0.7 : 1,
            marginTop: '2px',
            transition: 'all 0.3s ease',
            transform: isDisabled ? 'none' : 'scale(1)',
            boxShadow: isDisabled ? 'none' : '0 4px 15px rgba(255, 0, 80, 0.3)'
          }}
          onMouseDown={(e) => {
            if (!isDisabled) {
              e.target.style.transform = 'scale(0.98)';
            }
          }}
          onMouseUp={(e) => {
            if (!isDisabled) {
              e.target.style.transform = 'scale(1)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDisabled) {
              e.target.style.transform = 'scale(1)';
            }
          }}
        >
          {UI.loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
             Creating Account...
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              Sign Up for ChessThread
            </div>
          )}
        </button>
      </form>

      {/* Login Link */}
      <div style={{
        marginTop: '10px',
        textAlign: 'center',
        flexShrink: 0,
      }}>
        <span style={{ color: '#888', fontSize: '16px', marginRight: '5px' }}>
          Already have an account?{' '}
        </span>
        <Link
          to="/login"
          style={{
            color: 'gold',
            fontSize: '16px',
            fontWeight: 'bold',
            textDecoration: 'none'
          }}
        >
          Log In
        </Link>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  UI: state.UI
});

const mapDispatchToProps = {
  signupUser
};

export default connect(mapStateToProps, mapDispatchToProps)(SignupMobile);